import { useState, useEffect, useCallback } from 'react';
import { Zap, Check, X, ArrowRight, Trophy } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { leadService } from '../../services/api';
import { Skeleton, Badge, Button, Toast } from '../../components/ui';
import { STATUS_NAMES } from '../../constants';

const PROTECTED_STATUSES = ['REGISTRATION_COMPLETED', 'ADMISSION_COMPLETED'];

const LeadProgressPath = ({ leadId, onUpdated }) => {
  const { dark } = useTheme();
  const { user, hasRole, loadUser } = useAuth();
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingStage, setPendingStage] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  const fetchProgress = useCallback(async () => {
    try {
      const { data } = await leadService.getProgress(leadId);
      setProgress(data.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => { fetchProgress(); }, [fetchProgress]);

  const isOwner = progress?.assignedCounsellor?.id === user?._id;
  const isPrivileged = hasRole('RETAIL_OU_LEAD', 'SUPER_ADMIN');
  const canDrive = ((hasRole('COUNSELLOR') && isOwner) || isPrivileged) && !progress?.isLost;
  const isLifecycleComplete = progress?.currentStageCode === 'ADMISSION_COMPLETED';

  const confirmChange = async () => {
    if (!pendingStage) return;
    setSubmitting(true);
    try {
      const { data } = await leadService.updateStatus(leadId, { statusId: pendingStage.id, remarks: remarks || undefined });
      const msg = pendingStage.code === 'ADMISSION_COMPLETED'
        ? 'Admission completed — lead lifecycle closed!'
        : (data.data?.pointAwarded ? 'Status updated — performance point awarded!' : 'Status updated successfully');
      setToast({ msg, type: 'success' });
      setPendingStage(null);
      setRemarks('');
      await fetchProgress();
      if (data.data?.pointAwarded) await loadUser();
      if (onUpdated) onUpdated();
    } catch (err) {
      setToast({ msg: err.response?.data?.message || err.message || 'Failed to update status', type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Skeleton rows={3} cols={2} />;
  if (!progress) return null;

  return (
    <div style={{ background: dark ? '#0F172A' : '#F8FAFC', borderRadius: 14, border: `1px solid ${dark ? '#334155' : '#E2E8F0'}`, padding: '18px 20px 6px', marginBottom: 20, position: 'relative' }}>
      {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: dark ? '#F1F5F9' : '#0F172A', display: 'flex', alignItems: 'center', gap: 6 }}>
          <Zap size={14} color="#3B82F6" /> Lead Journey
        </div>
        {progress.isLost && <Badge color="#EF4444">Lost at {progress.lostFromCode ? STATUS_NAMES[progress.lostFromCode] : 'unknown stage'}</Badge>}
        {isLifecycleComplete && <Badge color="#059669">Lifecycle Complete</Badge>}
        {!progress.isLost && !isLifecycleComplete && canDrive && <Badge color="#3B82F6" variant="outline">You can update this lead</Badge>}
      </div>

      {progress.stages.map((s, i) => {
        const isLast = i === progress.stages.length - 1 && !progress.isLost;
        const completed = s.state === 'completed';
        const active = s.state === 'active';
        const stopped = s.state === 'stopped';
        const dotColor = completed ? '#22C55E' : active ? '#3B82F6' : stopped ? '#EF4444' : (dark ? '#475569' : '#CBD5E1');
        return (
          <div key={s.code} style={{ display: 'flex', gap: 14 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{
                width: 26, height: 26, borderRadius: 13, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: completed ? '#22C55E' : stopped ? '#EF4444' : 'transparent',
                border: `2px solid ${dotColor}`,
                boxShadow: active ? '0 0 0 4px #3B82F62A' : 'none',
                animation: active ? 'pulse 1.6s infinite' : 'none',
                flexShrink: 0, transition: 'all 0.2s',
              }}>
                {completed && <Check size={14} color="#fff" />}
                {stopped && <X size={14} color="#fff" />}
                {active && <div style={{ width: 8, height: 8, borderRadius: 4, background: '#3B82F6' }} />}
              </div>
              {!isLast && <div style={{ width: 2, flex: 1, minHeight: 32, background: completed ? '#22C55E' : (dark ? '#334155' : '#E2E8F0'), transition: 'background 0.2s' }} />}
            </div>
            <div style={{ paddingBottom: 26, flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: (active || completed) ? 700 : 500, color: (active || completed) ? (dark ? '#F1F5F9' : '#0F172A') : (dark ? '#64748B' : '#94A3B8') }}>
                {s.name}
              </div>
              {s.completedAt && (
                <>
                  <div style={{ fontSize: 11, color: dark ? '#64748B' : '#94A3B8', marginTop: 2 }}>
                    {new Date(s.completedAt).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })} · {s.completedBy}
                  </div>
                  {s.remarks && (
                    <div style={{ fontSize: 11, color: dark ? '#94A3B8' : '#64748B', marginTop: 2, fontStyle: 'italic' }}>
                      Remarks: {s.remarks}
                    </div>
                  )}
                </>
              )}
              {active && !stopped && !isLifecycleComplete && (
                canDrive ? (
                  <div style={{ marginTop: 10 }}>
                    {!pendingStage && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {(() => {
                          const base = progress.nextStages.filter(ns => ns.code !== 'LOST_LEAD');
                          const extra = isPrivileged
                            ? (progress.privilegedStatuses || []).filter(ps => !base.some(ns => ns.code === ps.code))
                            : [];
                          return [...base, ...extra]
                            .filter(ns => isPrivileged || !PROTECTED_STATUSES.includes(ns.code))
                            .map(ns => (
                              <Button key={ns.code} size="sm" icon={ArrowRight} onClick={() => { setPendingStage(ns); setRemarks(''); }}>
                                Move to {ns.name}
                              </Button>
                            ));
                        })()}
                        {progress.lostStatusId && (
                          <Button size="sm" variant="outline" style={{ color: '#EF4444', borderColor: '#EF444460' }}
                            onClick={() => { setPendingStage({ id: progress.lostStatusId, code: 'LOST_LEAD', name: 'Lost Lead' }); setRemarks(''); }}>
                            Mark Lost
                          </Button>
                        )}
                      </div>
                    )}

                    {pendingStage && (
                      <div style={{
                        marginTop: 4,
                        padding: '14px 16px',
                        borderRadius: 10,
                        border: `1px solid ${
                          pendingStage.code === 'LOST_LEAD' ? '#FCA5A5'
                          : pendingStage.code === 'ADMISSION_COMPLETED' ? '#A7F3D0'
                          : '#93C5FD'
                        }`,
                        background:
                          pendingStage.code === 'LOST_LEAD' ? (dark ? '#2D0A0A' : '#FEF2F2')
                          : pendingStage.code === 'ADMISSION_COMPLETED' ? (dark ? '#052E16' : '#F0FDF4')
                          : (dark ? '#0F1E3A' : '#EFF6FF'),
                      }}>
                        <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8, color:
                          pendingStage.code === 'LOST_LEAD' ? '#EF4444'
                          : pendingStage.code === 'ADMISSION_COMPLETED' ? '#059669'
                          : '#3B82F6'
                        }}>
                          {pendingStage.code === 'LOST_LEAD'
                            ? 'Mark as Lost Lead'
                            : pendingStage.code === 'ADMISSION_COMPLETED'
                              ? 'Complete Admission — this closes the lead lifecycle'
                              : `Move to "${pendingStage.name}"`}
                        </div>
                        <textarea
                          value={remarks}
                          onChange={e => setRemarks(e.target.value)}
                          placeholder="Add a remark about this update... (optional)"
                          rows={2}
                          style={{
                            width: '100%',
                            padding: '8px 10px',
                            borderRadius: 7,
                            fontSize: 12,
                            border: `1px solid ${dark ? '#334155' : '#D1D5DB'}`,
                            background: dark ? '#1E293B' : '#fff',
                            color: dark ? '#F1F5F9' : '#0F172A',
                            outline: 'none',
                            resize: 'vertical',
                            boxSizing: 'border-box',
                            fontFamily: 'inherit',
                          }}
                        />
                        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                          <Button
                            size="sm"
                            variant={pendingStage.code === 'LOST_LEAD' ? 'danger' : 'success'}
                            disabled={submitting}
                            onClick={confirmChange}
                          >
                            {submitting ? 'Saving...' : 'Confirm'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={submitting}
                            onClick={() => { setPendingStage(null); setRemarks(''); }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ fontSize: 11, color: dark ? '#64748B' : '#94A3B8', marginTop: 4, fontStyle: 'italic' }}>Currently here</div>
                )
              )}
            </div>
          </div>
        );
      })}

      {progress.isLost && (
        <div style={{ display: 'flex', gap: 14 }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ width: 26, height: 26, borderRadius: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#EF4444', flexShrink: 0 }}>
              <X size={14} color="#fff" />
            </div>
          </div>
          <div style={{ paddingBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#EF4444' }}>Lead Lost</div>
            {progress.lostAt && (
              <>
                <div style={{ fontSize: 11, color: dark ? '#64748B' : '#94A3B8', marginTop: 2 }}>
                  {new Date(progress.lostAt).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </div>
                {progress.lostRemarks && (
                  <div style={{ fontSize: 11, color: dark ? '#94A3B8' : '#64748B', marginTop: 2, fontStyle: 'italic' }}>
                    Remarks: {progress.lostRemarks}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {isLifecycleComplete && (
        <div style={{ display: 'flex', gap: 14, paddingBottom: 12 }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ width: 26, height: 26, borderRadius: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#059669', flexShrink: 0 }}>
              <Trophy size={13} color="#fff" />
            </div>
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#059669' }}>Admission Complete — Lifecycle Closed</div>
            <div style={{ fontSize: 11, color: dark ? '#64748B' : '#94A3B8', marginTop: 2 }}>This lead has been fully converted.</div>
          </div>
        </div>
      )}

    </div>
  );
};

export default LeadProgressPath;
